/**
 * Ce qu'un dossier attend — chunk 9.BG.1.
 *
 * ## Ce que ce composant remplace
 *
 * `ProjectStatusBadge`, qui lisait `projects.status`. Cette colonne porte huit
 * valeurs dont **une seule est écrite par le code** (`archived`) : les 26
 * dossiers vivants de la pré-prod étaient tous `draft`, y compris ceux dont la
 * demande était partie et dont on attendait trois assureurs. Le badge ne se
 * trompait pas — il ne disait rien.
 *
 * ## Le principe
 *
 * **Une ligne dit ce qu'elle attend, pas ce qu'elle est.** L'état vient du
 * serveur (`app.project_operational`, migration 0082), dérivé des demandes, des
 * destinataires, des réponses et des échéances. Rien n'est calculé ici : le web
 * qui recalculerait un état produirait un second avis, et le jour où les deux
 * divergent, personne ne saurait lequel croire.
 *
 * ## L'action suit l'état
 *
 * Le raccourci n'est pas décoratif : il change avec la situation. Préparer,
 * reprendre, suivre, comparer — le conseiller n'a pas à deviner quel écran
 * ouvrir.
 */

import type { ProjectOperationalState } from "@kollectivia/shared";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { dossierPreparationHref } from "../../../lib/preparationNavigation";

/** Le ton suit l'attente : ce qui bloque, ce qui avance, ce qui est prêt. */
const TON: Record<ProjectOperationalState, string> = {
  to_prepare: "bg-slate-100 text-slate-700",
  preparing: "bg-brand-50 text-brand-800",
  awaiting_insurers: "bg-amber-50 text-amber-900",
  partial_responses: "bg-amber-50 text-amber-900",
  ready_to_compare: "bg-emerald-50 text-emerald-800",
};

export function EtatOperationnelBadge(props: {
  readonly etat: ProjectOperationalState | undefined;
  readonly activityScope?: "history" | "current";
  readonly enRetard?: boolean | null;
}) {
  const { t } = useTranslation();
  if (!props.etat) return <span className="text-slate-400">—</span>;

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span
        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${TON[props.etat]}`}
      >
        {t(
          props.activityScope === "history"
            ? "projects.operational.history"
            : `projects.operational.${props.etat}`,
        )}
      </span>
      {/* Le retard est un DRAPEAU, pas un état : un dossier en retard attend
          toujours la même chose, il l'attend seulement depuis trop longtemps. */}
      {props.enRetard && (
        <span className="inline-flex rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800">
          {t("projects.operational.late")}
        </span>
      )}
    </span>
  );
}

/** Où en sont les assureurs, en une ligne. Rien à afficher si aucun n'est sollicité. */
export function EtatOperationnelDetail(props: {
  readonly sollicites?: number;
  readonly historique?: number;
  readonly recues?: number;
  readonly attente?: number;
  readonly echeance?: string | null;
}) {
  const { t, i18n } = useTranslation();
  const sollicites = props.sollicites ?? 0;
  if (sollicites === 0 && !props.echeance && !props.historique) return null;

  return (
    <span className="text-xs text-slate-500">
      {!!props.historique && (
        <span className="block">
          {t("projects.operational.history_count", { count: props.historique })}
        </span>
      )}
      {sollicites > 0 &&
        t("projects.operational.counts", {
          solicited: sollicites,
          received: props.recues ?? 0,
          awaiting: props.attente ?? 0,
        })}
      {sollicites > 0 && props.echeance && " · "}
      {props.echeance && (
        <span className="tabular-nums">
          {t("projects.operational.due_on", {
            date: new Date(props.echeance).toLocaleDateString(i18n.language),
          })}
        </span>
      )}
    </span>
  );
}

/**
 * Le raccourci qui correspond à l'état.
 *
 * Un lien générique « Voir » obligerait à ouvrir le dossier pour découvrir quoi
 * y faire — c'est précisément ce que cette liste doit éviter.
 */
export function EtatOperationnelAction(props: {
  readonly projectId: string;
  readonly etat: ProjectOperationalState | undefined;
  readonly activityScope?: "history" | "current";
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const sameProject = location.pathname === `/projects/${props.projectId}`;
  const preparationUrl = dossierPreparationHref(
    props.projectId,
    sameProject ? params.get("request") : undefined,
    sameProject ? params.get("request_section") : undefined,
  );
  const classe =
    "focus-visible:ring-brand-500 inline-flex min-h-11 items-center touch-manipulation rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2";

  if (props.activityScope === "history")
    return (
      <Link to={preparationUrl} className={classe}>
        {t("projects.operational.cta.history")}
      </Link>
    );
  switch (props.etat) {
    case "to_prepare":
      return (
        <Link to={preparationUrl} className={classe}>
          {t("projects.operational.cta.prepare")}
        </Link>
      );
    case "preparing":
      return (
        <Link to={preparationUrl} className={classe}>
          {t("projects.operational.cta.resume")}
        </Link>
      );
    case "awaiting_insurers":
    case "partial_responses":
      return (
        <Link to={`/projects/${props.projectId}?view=insurers`} className={classe}>
          {t("projects.operational.cta.track")}
        </Link>
      );
    case "ready_to_compare":
      return (
        <Link to={`/projects/${props.projectId}/comparison`} className={classe}>
          {t("projects.operational.cta.compare")}
        </Link>
      );
    default:
      return null;
  }
}
